'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DonationFormData {
  amount: number;
  customAmount: string;
  frequency: string;
  campaign: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  paymentPlatform: string;
  anonymous: boolean;
  newsletter: boolean;
}

interface DonationOption {
  amount: number;
  impact: string;
  icon: string;
}

interface Campaign {
  id: string;
  name: string;
  description: string;
  icon: string;
}

const MPESA_NUMBER = '+255759595968';

const EastAfricaDonation = () => {
  const [selectedAmount, setSelectedAmount] = useState<number>(10000);
  const [customAmount, setCustomAmount] = useState('');
  const [copied, setCopied] = useState(false);

  const donationOptions = [
    { amount: 5000, label: 'TZS 5,000', impact: 'Supports a family for a week' },
    { amount: 10000, label: 'TZS 10,000', impact: 'Provides clean water for 5 families' },
    { amount: 25000, label: 'TZS 25,000', impact: 'Supplies medical care for 10 children' },
    { amount: 50000, label: 'TZS 50,000', impact: 'Feeds 20 families for two weeks' },
    { amount: 100000, label: 'TZS 100,000', impact: 'Builds emergency shelter for a family' },
    { amount: 250000, label: 'TZS 250,000', impact: 'Establishes community health support' },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(MPESA_NUMBER);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayAmount = customAmount ? customAmount : selectedAmount.toLocaleString();

  return (
    <div className="space-y-8">
      {/* M-Pesa Header Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center space-x-4 mb-3">
          <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="DevicePhoneMobileIcon" size={32} className="text-green-600" variant="solid" />
          </div>
          <div>
            <h3 className="text-2xl font-headline font-bold">M-Pesa Donation</h3>
            <p className="text-green-100 font-body text-sm">Vodacom Tanzania — Fast & Secure Mobile Payment</p>
          </div>
        </div>
        <p className="text-green-100 font-body text-sm leading-relaxed">
          Send your donation directly via Vodacom M-Pesa. Your contribution reaches us instantly and securely.
        </p>
      </div>

      {/* Select Amount */}
      <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
        <h3 className="text-xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="CurrencyDollarIcon" size={24} className="text-primary" variant="solid" />
          <span>Select Donation Amount (TZS)</span>
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {donationOptions.map((option) => (
            <button
              key={option.amount}
              type="button"
              onClick={() => { setSelectedAmount(option.amount); setCustomAmount(''); }}
              className={`p-4 rounded-xl border-2 transition-all duration-300 text-left relative ${
                selectedAmount === option.amount && !customAmount
                  ? 'border-green-600 bg-green-50 scale-105' :'border-border hover:border-green-400 hover:bg-muted'
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <Icon
                  name={option.icon as any}
                  size={24}
                  className={selectedAmount === option.amount && !customAmount ? 'text-primary' : 'text-muted-foreground'}
                  variant="solid"
                />
                <span className="text-2xl font-headline font-bold text-foreground">
                  {option.label}
                </span>
              </div>
              <p className="text-sm text-muted-foreground font-body">{option.impact}</p>
            </button>
          ))}
        </div>

        <div>
          <label className="block text-sm font-cta font-bold text-foreground mb-2">
            Or Enter Custom Amount (TZS)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-body text-sm">TZS</span>
            <input
              type="number"
              value={customAmount}
              onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(0); }}
              placeholder="Enter amount"
              className="w-full pl-14 pr-4 py-4 border-2 border-border rounded-lg font-body text-lg focus:outline-none focus:border-green-500 transition-colors duration-300"
              min="1"
            />
          </div>
        </div>
      </div>

      {/* M-Pesa Payment Instructions */}
      <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
        <h3 className="text-xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="InformationCircleIcon" size={24} className="text-green-600" variant="solid" />
          <span>How to Send via M-Pesa</span>
        </h3>

        {/* Recipient Number */}
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-6">
          <p className="text-sm font-cta font-bold text-green-700 mb-2 uppercase tracking-wide">
            Vodacom M-Pesa Recipient Number
          </p>
          <div className="flex items-center justify-between">
            <span className="text-3xl font-headline font-bold text-green-800 tracking-widest">
              {MPESA_NUMBER}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg font-cta font-bold text-sm transition-all hover:bg-green-700 active:scale-95"
            >
              <Icon name={copied ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={18} variant="solid" />
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
          <p className="text-xs text-green-600 font-body mt-2">
            Account Name: SIGLINE Foundation
          </p>
        </div>

        {/* Step-by-step instructions */}
        <div className="space-y-4">
          <h4 className="font-cta font-bold text-foreground text-sm uppercase tracking-wide">Step-by-Step Instructions:</h4>
          {[
            { step: 1, title: 'Open M-Pesa on your phone', desc: 'Dial *150*00# or open your Vodacom M-Pesa app' },
            { step: 2, title: 'Select "Send Money"', desc: 'Choose the "Send Money" or "Tuma Pesa" option from the menu' },
            { step: 3, title: 'Enter the recipient number', desc: `Enter ${MPESA_NUMBER} as the recipient` },
            { step: 4, title: 'Enter the amount', desc: `Enter TZS ${displayAmount} or your chosen donation amount` },
            { step: 5, title: 'Add a reference', desc: 'Type "DONATION" or your name as the reference/description' },
            { step: 6, title: 'Confirm and send', desc: 'Enter your M-Pesa PIN to confirm and complete the transaction' },
          ].map((item) => (
            <div key={item.step} className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-cta font-bold text-sm flex-shrink-0 mt-0.5">
                {item.step}
              </div>
              <div>
                <p className="font-cta font-bold text-foreground text-sm">{item.title}</p>
                <p className="text-sm text-muted-foreground font-body">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation Note */}
      <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <Icon name="CheckCircleIcon" size={24} className="text-primary flex-shrink-0 mt-0.5" variant="solid" />
          <div>
            <h4 className="font-cta font-bold text-foreground mb-1">After Sending</h4>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              You will receive an M-Pesa confirmation SMS. Please save it as your donation receipt. For large donations or to receive an official receipt, contact us at{' '}
              <span className="font-bold text-primary">sigday@noenemy.life</span> with your transaction ID.
            </p>
          </div>
        </div>
      </div>

      {/* Countries Covered */}
      <div className="bg-muted/50 rounded-xl p-5 border border-border">
        <h4 className="font-cta font-bold text-foreground text-sm mb-3 flex items-center space-x-2">
          <Icon name="GlobeAltIcon" size={18} className="text-green-600" variant="solid" />
          <span>East Africa Coverage</span>
        </h4>
        <div className="flex flex-wrap gap-2">
          {['🇹🇿 Tanzania', '🇰🇪 Kenya', '🇺🇬 Uganda', '🇷🇼 Rwanda', '🇧🇮 Burundi', '🇸🇸 South Sudan', '🇪🇹 Ethiopia'].map((country) => (
            <span key={country} className="px-3 py-1 bg-white border border-border rounded-full text-sm font-body text-foreground">
              {country}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground font-body mt-3">
          * M-Pesa is available across East Africa. International M-Pesa transfers may apply for non-Tanzania senders.
        </p>
      </div>
    </div>
  );
};

const DonationForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'east-africa' | 'other-countries'>('east-africa');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState<DonationFormData>({
    amount: 50,
    customAmount: '',
    frequency: 'one-time',
    campaign: 'general',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    paymentPlatform: '',
    anonymous: false,
    newsletter: true,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const donationOptions: DonationOption[] = [
    {
      amount: 25,
      impact: 'Provides clean water for 5 families for a month',
      icon: 'BeakerIcon'
    },
    {
      amount: 50,
      impact: 'Supplies medical care for 10 children',
      icon: 'HeartIcon'
    },
    {
      amount: 100,
      impact: 'Feeds 20 families for two weeks',
      icon: 'ShoppingBagIcon'
    },
    {
      amount: 250,
      impact: 'Builds emergency shelter for 3 families',
      icon: 'HomeIcon'
    },
    {
      amount: 500,
      impact: 'Provides education supplies for 50 students',
      icon: 'AcademicCapIcon'
    },
    {
      amount: 1000,
      impact: 'Establishes community health clinic',
      icon: 'BuildingOfficeIcon'
    }
  ];

  const frequencies = [
    { id: 'one-time', label: 'One-Time', description: 'Single donation' },
    { id: 'monthly', label: 'Monthly', description: 'Recurring monthly' },
    { id: 'quarterly', label: 'Quarterly', description: 'Every 3 months' },
    { id: 'annually', label: 'Annually', description: 'Once per year' }
  ];

  const campaigns: Campaign[] = [
    {
      id: 'general',
      name: 'Where Most Needed',
      description: 'Support urgent priorities across all programs',
      icon: 'SparklesIcon'
    },
    {
      id: 'emergency',
      name: 'Emergency Response',
      description: 'Rapid response to humanitarian crises',
      icon: 'ExclamationTriangleIcon'
    },
    {
      id: 'food',
      name: 'Food Security',
      description: 'Combat hunger and malnutrition',
      icon: 'ShoppingBagIcon'
    },
    {
      id: 'water',
      name: 'Clean Water Access',
      description: 'Provide safe drinking water',
      icon: 'BeakerIcon'
    },
    {
      id: 'health',
      name: 'Healthcare Services',
      description: 'Medical care and health programs',
      icon: 'HeartIcon'
    },
    {
      id: 'education',
      name: 'Education Programs',
      description: 'Support learning and development',
      icon: 'AcademicCapIcon'
    },
    {
      id: 'shelter',
      name: 'Emergency Shelter',
      description: 'Safe housing for displaced families',
      icon: 'HomeIcon'
    },
    {
      id: 'reconciliation',
      name: 'Peace & Reconciliation',
      description: 'Healing and community building',
      icon: 'HandRaisedIcon'
    }
  ];

  const paymentPlatforms = [
    {
      id: 'paypal',
      name: 'PayPal',
      description: 'International donors - Credit/Debit cards',
      icon: 'CreditCardIcon',
      recommended: 'Best for international'
    },
    {
      id: 'flutterwave',
      name: 'Flutterwave',
      description: 'African donors - Mobile Money & Cards',
      icon: 'DevicePhoneMobileIcon',
      recommended: 'Best for Africa'
    }
  ];

  const handleAmountSelect = (amount: number) => {
    if (!isHydrated) return;
    setFormData({ ...formData, amount, customAmount: '' });
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHydrated) return;
    setFormData({ ...formData, customAmount: e.target.value, amount: 0 });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!isHydrated) return;
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const getCurrentAmount = () => {
    return formData.customAmount ? parseFloat(formData.customAmount) : formData.amount;
  };

  const handleNextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const handlePrevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
    }, 2000);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <p className="text-lg text-muted-foreground font-body">Loading donation form...</p>
          </div>
        </div>
      </section>
    );
  }

  if (submitSuccess) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="bg-card rounded-xl p-12 shadow-lg border border-border text-center">
            <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} className="text-success" variant="solid" />
            </div>
            <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
              Thank You for Your Generosity!
            </h2>
            <p className="text-lg text-muted-foreground font-body mb-6">
              Your donation of <span className="font-bold text-primary">${getCurrentAmount()}</span> will make a real difference.
            </p>
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-8">
              <p className="text-sm text-foreground font-body">
                <Icon name="InformationCircleIcon" size={20} className="inline text-primary mr-2" variant="solid" />
                You will receive a confirmation email shortly with your donation receipt and impact tracking details.
              </p>
            </div>
            <button
              onClick={() => {
                setSubmitSuccess(false);
                setCurrentStep(1);
                setFormData({
                  amount: 50,
                  customAmount: '',
                  frequency: 'one-time',
                  campaign: 'general',
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  country: '',
                  paymentPlatform: '',
                  anonymous: false,
                  newsletter: true,
                });
              }}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-primary/90 hover:shadow-md"
            >
              Make Another Donation
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Tab Navigation */}
        <div className="max-w-5xl mx-auto mb-10">
          <div className="flex rounded-xl overflow-hidden border-2 border-border shadow-md">
            <button
              type="button"
              onClick={() => setActiveTab('east-africa')}
              className={`flex-1 flex items-center justify-center space-x-3 py-5 px-6 font-cta font-bold text-base transition-all duration-300 ${
                activeTab === 'east-africa' ?'bg-green-600 text-white shadow-inner' :'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name="DevicePhoneMobileIcon" size={22} variant="solid" />
              <div className="text-left">
                <div className="text-sm font-bold">🌍 East Africa</div>
                <div className={`text-xs font-normal ${activeTab === 'east-africa' ? 'text-green-100' : 'text-muted-foreground'}`}>
                  M-Pesa (Vodacom)
                </div>
              </div>
            </button>
            <div className="w-px bg-border" />
            <button
              type="button"
              onClick={() => setActiveTab('other-countries')}
              className={`flex-1 flex items-center justify-center space-x-3 py-5 px-6 font-cta font-bold text-base transition-all duration-300 ${
                activeTab === 'other-countries' ?'bg-primary text-primary-foreground shadow-inner' :'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name="GlobeAltIcon" size={22} variant="solid" />
              <div className="text-left">
                <div className="text-sm font-bold">🌐 Other Countries</div>
                <div className={`text-xs font-normal ${activeTab === 'other-countries' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  International Payment
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* East Africa Tab */}
        {activeTab === 'east-africa' && (
          <div className="max-w-5xl mx-auto">
            <EastAfricaDonation />
          </div>
        )}

        {/* Other Countries Tab */}
        {activeTab === 'other-countries' && (
          <div className="max-w-5xl mx-auto">
            {/* Coming Soon Notice */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-2 border-primary/30 rounded-xl p-6 mb-8 shadow-md">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="ClockIcon" size={28} className="text-primary" variant="solid" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-cta font-bold text-foreground mb-1">
                    International Payment Methods — Coming Soon
                  </h4>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    We are currently finalizing our international payment integrations (PayPal, Stripe, Flutterwave, and more). 
                    These will be fully activated once our payment agreements are confirmed. 
                    Thank you for your patience and continued support!
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['PayPal', 'Stripe', 'Flutterwave', 'Bank Transfer', 'Credit/Debit Card'].map((method) => (
                      <span key={method} className="px-3 py-1 bg-white border border-primary/20 rounded-full text-xs font-cta font-bold text-primary">
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center font-cta font-bold transition-all duration-300 ${
                          currentStep >= step
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {step}
                      </div>
                      <span className="text-xs font-cta font-bold text-muted-foreground mt-2">
                        {step === 1 && 'Amount'}
                        {step === 2 && 'Campaign'}
                        {step === 3 && 'Details'}
                        {step === 4 && 'Payment'}
                      </span>
                    </div>
                    {step < 4 && (
                      <div
                        className={`h-1 flex-1 transition-all duration-300 ${
                          currentStep > step ? 'bg-primary' : 'bg-muted'
                        }`}
                      ></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
              {/* Step 1: Amount & Frequency */}
              {currentStep === 1 && (
                <div className="space-y-8 animate-fade-in">
                  <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                      <Icon name="CurrencyDollarIcon" size={28} className="text-primary" variant="solid" />
                      <span>Select Donation Amount</span>
                    </h3>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                      {donationOptions.map((option) => (
                        <button
                          key={option.amount}
                          type="button"
                          onClick={() => handleAmountSelect(option.amount)}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 text-left relative ${
                            formData.amount === option.amount && !formData.customAmount
                              ? 'border-primary bg-primary/10 scale-105' :'border-border hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <Icon
                              name={option.icon as any}
                              size={24}
                              className={formData.amount === option.amount && !formData.customAmount ? 'text-primary' : 'text-muted-foreground'}
                              variant="solid"
                            />
                            <span className="text-2xl font-headline font-bold text-foreground">
                              ${option.amount}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground font-body">{option.impact}</p>
                        </button>
                      ))}
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-cta font-bold text-foreground mb-2">
                        Or Enter Custom Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">
                          $
                        </span>
                        <input
                          type="number"
                          name="customAmount"
                          value={formData.customAmount}
                          onChange={handleCustomAmountChange}
                          placeholder="Enter amount"
                          className="w-full pl-10 pr-4 py-4 border-2 border-border rounded-lg font-body text-lg focus:outline-none focus:border-primary transition-colors duration-300"
                          min="1"
                        />
                      </div>
                    </div>

                    {(formData.amount > 0 || formData.customAmount) && (
                      <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                        <p className="text-sm font-body text-foreground leading-relaxed">
                          <Icon name="InformationCircleIcon" size={20} className="inline text-primary mr-2" variant="solid" />
                          {donationOptions.find(opt => opt.amount === formData.amount)?.impact ||
                           `Your generous contribution of ${getCurrentAmount()} will directly support vulnerable communities in need.`}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                      <Icon name="CalendarIcon" size={28} className="text-primary" variant="solid" />
                      <span>Donation Frequency</span>
                    </h3>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {frequencies.map((freq) => (
                        <button
                          key={freq.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, frequency: freq.id })}
                          className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                            formData.frequency === freq.id
                              ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          <div className="text-center">
                            <div className="font-cta font-bold text-foreground mb-1">
                              {freq.label}
                            </div>
                            <div className="text-xs text-muted-foreground font-body">
                              {freq.description}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {formData.frequency !== 'one-time' && (
                      <div className="mt-4 bg-success/10 border border-success/30 rounded-lg p-4">
                        <p className="text-sm font-body text-foreground">
                          <Icon name="CheckCircleIcon" size={20} className="inline text-success mr-2" variant="solid" />
                          Recurring donations provide sustainable support and maximize your impact over time. You can cancel anytime.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={getCurrentAmount() <= 0}
                      className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue to Campaign Selection
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Campaign Selection */}
              {currentStep === 2 && (
                <div className="space-y-8 animate-fade-in">
                  <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                      <Icon name="TagIcon" size={28} className="text-primary" variant="solid" />
                      <span>Choose Your Campaign</span>
                    </h3>

                    <p className="text-muted-foreground font-body mb-6">
                      Select where you'd like your donation to make the most impact
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {campaigns.map((campaign) => (
                        <button
                          key={campaign.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, campaign: campaign.id })}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 text-left relative ${
                            formData.campaign === campaign.id
                              ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          {campaign.recommended && (
                            <div className="absolute -top-3 right-4 px-3 py-1 bg-success text-success-foreground text-xs font-cta font-bold rounded-full">
                              {campaign.recommended}
                            </div>
                          )}
                          <div className="flex items-start space-x-4">
                            <div className={`p-3 rounded-lg ${
                              formData.campaign === campaign.id ? 'bg-primary/20' : 'bg-muted'
                            }`}>
                              <Icon
                                name={campaign.icon as any}
                                size={24}
                                className={formData.campaign === campaign.id ? 'text-primary' : 'text-muted-foreground'}
                                variant="solid"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-cta font-bold text-foreground mb-1">
                                {campaign.name}
                              </h4>
                              <p className="text-sm text-muted-foreground font-body">
                                {campaign.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-muted text-foreground rounded-md font-cta font-bold transition-colors hover:bg-muted/80"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md"
                    >
                      Continue to Your Details
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Donor Information */}
              {currentStep === 3 && (
                <div className="space-y-8 animate-fade-in">
                  <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                      <Icon name="UserIcon" size={28} className="text-primary" variant="solid" />
                      <span>Your Information</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-cta font-bold text-foreground mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-body"
                          placeholder="John"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-cta font-bold text-foreground mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-body"
                          placeholder="Doe"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-cta font-bold text-foreground mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-body"
                          placeholder="john.doe@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-cta font-bold text-foreground mb-2">
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-body"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-cta font-bold text-foreground mb-2">
                          Country *
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-body"
                          placeholder="United States"
                        />
                      </div>
                    </div>

                    <div className="mt-6 space-y-4">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="anonymous"
                          checked={formData.anonymous}
                          onChange={handleInputChange}
                          className="mt-1 w-5 h-5 text-primary border-input rounded focus:ring-2 focus:ring-ring"
                        />
                        <span className="text-sm text-foreground font-body">
                          Make this donation anonymous (your name won't be displayed publicly)
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="newsletter"
                          checked={formData.newsletter}
                          onChange={handleInputChange}
                          className="mt-1 w-5 h-5 text-primary border-input rounded focus:ring-2 focus:ring-ring"
                        />
                        <span className="text-sm text-foreground font-body">
                          Subscribe to our newsletter for impact updates and stories from the field
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-muted text-foreground rounded-md font-cta font-bold transition-colors hover:bg-muted/80"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.country}
                      className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Payment Platform Selection */}
              {currentStep === 4 && (
                <div className="space-y-8 animate-fade-in">
                  {/* Payment System In Progress Notice */}
                  <div className="bg-warning/10 border-2 border-warning/50 rounded-xl p-6 shadow-lg">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <Icon name="ExclamationTriangleIcon" size={32} className="text-warning" variant="solid" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-cta font-bold text-foreground mb-2">
                          Payment System Integration In Progress
                        </h4>
                        <p className="text-sm text-muted-foreground font-body leading-relaxed">
                          We're currently finalizing our payment agreement and integration. The payment processing system is not yet active. Please check back soon or contact us directly to arrange your donation. Thank you for your patience and willingness to support our cause!
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
                    <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
                      <Icon name="CreditCardIcon" size={28} className="text-primary" variant="solid" />
                      <span>Choose Payment Method</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      {paymentPlatforms.map((platform) => (
                        <button
                          key={platform.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, paymentPlatform: platform.id })}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 text-left relative ${
                            formData.paymentPlatform === platform.id
                              ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          {platform.recommended && (
                            <div className="absolute -top-3 right-4 px-3 py-1 bg-success text-success-foreground text-xs font-cta font-bold rounded-full">
                              {platform.recommended}
                            </div>
                          )}
                          <div className="flex items-start space-x-4">
                            <div className={`p-3 rounded-lg ${
                              formData.paymentPlatform === platform.id ? 'bg-primary/20' : 'bg-muted'
                            }`}>
                              <Icon
                                name={platform.icon as any}
                                size={32}
                                className={formData.paymentPlatform === platform.id ? 'text-primary' : 'text-muted-foreground'}
                                variant="solid"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-cta font-bold text-foreground mb-2">
                                {platform.name}
                              </h4>
                              <p className="text-sm text-muted-foreground font-body">
                                {platform.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Donation Summary */}
                    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 border border-primary/20">
                      <h4 className="text-lg font-cta font-bold text-foreground mb-4">Donation Summary</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-body">Amount:</span>
                          <span className="text-xl font-headline font-bold text-foreground">${getCurrentAmount()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-body">Frequency:</span>
                          <span className="font-cta font-bold text-foreground capitalize">
                            {frequencies.find(f => f.id === formData.frequency)?.label}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-body">Campaign:</span>
                          <span className="font-cta font-bold text-foreground">
                            {campaigns.find(c => c.id === formData.campaign)?.name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-body">Payment Method:</span>
                          <span className="font-cta font-bold text-foreground">
                            {paymentPlatforms.find(p => p.id === formData.paymentPlatform)?.name || 'Not selected'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {formData.paymentPlatform && (
                      <div className="mt-6 bg-warning/10 border border-warning/30 rounded-lg p-4">
                        <p className="text-sm font-body text-foreground">
                          <Icon name="InformationCircleIcon" size={20} className="inline text-warning mr-2" variant="solid" />
                          You will be redirected to {paymentPlatforms.find(p => p.id === formData.paymentPlatform)?.name} to complete your secure payment.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-muted text-foreground rounded-md font-cta font-bold transition-colors hover:bg-muted/80"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.paymentPlatform || isSubmitting}
                      className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold transition-all duration-300 hover:bg-accent/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Icon name="LockClosedIcon" size={20} />
                          <span>Complete Secure Donation</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}

      </div>
    </section>
  );
};

export default DonationForm;